import { useRef, useEffect, useCallback, useState } from "react";

const MAX_IMAGE_DIMENSION = 1000; // px, longest side — client-side pre-compression
const MAX_IMAGE_BYTES = 1.5 * 1024 * 1024; // ~1.5MB per image, before backend re-validates/re-compresses

export function isRichTextEmpty(htmlValue = "") {
  const stripped = htmlValue
    .replace(/<br\s*\/?>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  return stripped.length === 0 && !/<img\b/i.test(htmlValue);
}

export function escapeHtml(text = "") {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Plain text (an AI draft/solution, a stored plain-text body) -> HTML safe to hand to the
// editor's `value`, which is assigned to innerHTML: escaped, with line breaks preserved.
export function plainTextToEditorHtml(text) {
  return escapeHtml(String(text ?? "")).replace(/\r?\n/g, "<br>");
}

function readFileDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("file read failed"));
    reader.readAsDataURL(file);
  });
}

export function dataUrlToFile(dataUrl, filename = "pasted-image") {
  const match = /^data:([^;,]+);base64,([A-Za-z0-9+/=]+)$/i.exec(dataUrl || "");
  if (!match) throw new Error("invalid image data");
  const bytes = Uint8Array.from(atob(match[2]), (char) => char.charCodeAt(0));
  const extension = match[1].toLowerCase() === "image/jpeg" ? "jpg" : match[1].split("/")[1];
  const baseName = filename.replace(/\.[^.]+$/, "") || "pasted-image";
  return new File([bytes], `${baseName}.${extension}`, { type: match[1] });
}

let pastedImageCounter = 0;

// Clipboard screenshots often arrive with an empty or generic ("image.png")
// filename; give those a unique, safe display name instead of colliding.
function uniqueImageFilename(providedName, mimeType) {
  const trimmed = (providedName || "").trim();
  const isGeneric = !trimmed || /^image\.(png|jpe?g|gif|webp)$/i.test(trimmed);
  if (!isGeneric) return trimmed;
  const extension = (mimeType || "").split("/")[1]?.replace("jpeg", "jpg") || "png";
  pastedImageCounter += 1;
  return `pasted-image-${Date.now()}-${pastedImageCounter}.${extension}`;
}

function imageDimensions(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => reject(new Error("invalid image"));
    img.src = dataUrl;
  });
}

async function fileToCompressedDataUrl(file) {
  const originalDataUrl = await readFileDataUrl(file);
  const originalDimensions = await imageDimensions(originalDataUrl);
  if (["image/png", "image/gif", "image/webp"].includes(file.type) && file.size <= MAX_IMAGE_BYTES) {
    return { dataUrl: originalDataUrl, ...originalDimensions };
  }

  return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Canvas-based resize/recompression can fail for reasons that have nothing to do with the
        // image itself (2d context unavailable — privacy hardening, exhausted context budget, a
        // disabled/broken canvas backend — or toDataURL throwing). Without this guard, an
        // exception thrown here happens inside a DOM event handler with no caller to catch it: the
        // surrounding Promise never resolves or rejects, so the caller awaits it forever and
        // processingCount (and any "uploading image..." UI it drives) gets stuck on forever too.
        try {
          let { width, height } = img;
          const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(width, height));
          width = Math.max(1, Math.round(width * scale));
          height = Math.max(1, Math.round(height * scale));
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("2d canvas context unavailable");
          ctx.drawImage(img, 0, 0, width, height);
          let quality = 0.85;
          let dataUrl = canvas.toDataURL("image/jpeg", quality);
          while (dataUrl.length * 0.75 > MAX_IMAGE_BYTES && quality > 0.4) {
            quality -= 0.15;
            dataUrl = canvas.toDataURL("image/jpeg", quality);
          }
          resolve({ dataUrl, width, height });
        } catch (err) {
          reject(err instanceof Error ? err : new Error("image compression failed"));
        }
      };
      img.onerror = () => reject(new Error("invalid image"));
      img.src = originalDataUrl;
  });
}

/**
 * Minimal contentEditable-based rich text editor: plain text, line breaks,
 * pasted images (auto-compressed client-side, re-validated server-side), and
 * native browser image resize (CSS `resize: both` on the wrapping span).
 */
export default function RichTextEditor({
  value,
  onChange,
  placeholder = "",
  minRows = 3,
  onError,
  className = "",
  id,
  autoFocus = false,
  allowImages = false,
  allowFormatting = false,
  onProcessingChange,
}) {
  const ref = useRef(null);
  const fileInputRef = useRef(null);
  const [processingCount, setProcessingCount] = useState(0);

  useEffect(() => {
    onProcessingChange?.(processingCount > 0);
  }, [processingCount, onProcessingChange]);
  // Starts undefined (not `value`) so the very first sync always runs — the
  // actual contentEditable DOM node is empty on mount regardless of the
  // initial `value` prop (e.g. editing an existing comment, or opening the
  // email composer with a signature already in `value`), so it must always
  // be populated at least once.
  const lastEmittedRef = useRef();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (value === lastEmittedRef.current) return;
    if (document.activeElement === el) return; // don't fight the user's cursor
    el.innerHTML = value || "";
    lastEmittedRef.current = value;
  }, [value]);

  const emitChange = useCallback(() => {
    const html = ref.current?.innerHTML || "";
    lastEmittedRef.current = html;
    onChange(html);
  }, [onChange]);

  // Untrusted plain text (pasted/dropped) inserted at the cursor as actual DOM nodes, never as
  // an HTML string: plainTextToEditorHtml's own escaping is the only thing that ever turns this
  // text into markup (just `<br>` for line breaks), so nothing here can become a live element.
  const insertPlainTextAtCursor = useCallback((text) => {
    const editor = ref.current;
    if (!editor) return;
    editor.focus();
    const sel = window.getSelection();
    const range = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
    const temp = document.createElement("div");
    temp.innerHTML = plainTextToEditorHtml(text);
    const fragment = document.createDocumentFragment();
    let lastNode = null;
    while (temp.firstChild) lastNode = fragment.appendChild(temp.firstChild);

    if (range && editor.contains(range.commonAncestorContainer)) {
      range.deleteContents();
      range.insertNode(fragment);
      if (lastNode) {
        range.setStartAfter(lastNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } else {
      editor.appendChild(fragment);
    }
    emitChange();
  }, [emitChange]);

  const insertImage = useCallback((dataUrl, width, height, filename = "") => {
    const editor = ref.current;
    if (!editor) return;
    editor.focus();
    const sel = window.getSelection();
    const range = sel && sel.rangeCount ? sel.getRangeAt(0) : null;

    const displayWidth = Math.min(width, 320);
    const displayHeight = Math.round(height * (displayWidth / width));

    const wrapper = document.createElement("span");
    wrapper.setAttribute("contenteditable", "false");
    wrapper.style.cssText =
      "display:inline-block;resize:both;overflow:hidden;max-width:100%;vertical-align:bottom;border:1px solid transparent;";
    wrapper.style.width = `${displayWidth}px`;
    wrapper.style.height = `${displayHeight}px`;

    const img = document.createElement("img");
    img.src = dataUrl;
    if (filename) img.dataset.filename = filename;
    img.style.cssText = "width:100%;height:100%;display:block;";
    img.draggable = false;
    wrapper.appendChild(img);

    if (range && editor.contains(range.commonAncestorContainer)) {
      range.deleteContents();
      range.insertNode(wrapper);
      range.setStartAfter(wrapper);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      editor.appendChild(wrapper);
    }
    emitChange();
  }, [emitChange]);

  const processImageFile = useCallback(async (file) => {
    if (!file?.type?.startsWith("image/")) return;
    setProcessingCount((count) => count + 1);
    try {
      const { dataUrl, width, height } = await fileToCompressedDataUrl(file);
      // Pasted/inserted images travel to the server as part of the editor's
      // own HTML (embedded data: URI, converted server-side to a persistent
      // attachment) — they must NOT also be queued as a separate multipart
      // file, which would send the same image twice per request and risk
      // tripping request-size limits for nothing.
      insertImage(dataUrl, width, height, uniqueImageFilename(file.name, file.type));
    } catch {
      onError?.("שגיאה בטעינת התמונה");
    } finally {
      setProcessingCount((count) => count - 1);
    }
  }, [insertImage, onError]);

  const handlePaste = useCallback(
    async (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const imageItems = items.filter((it) => it.type.startsWith("image/"));
      if (imageItems.length) {
        e.preventDefault();
        try {
          for (const imageItem of imageItems) {
            const file = imageItem.getAsFile();
            if (file) await processImageFile(file);
          }
        } catch {
          onError?.("שגיאה בהדבקת התמונה");
        }
        return;
      }
      // Never let the browser insert the clipboard's raw text/html representation into this
      // live, un-sandboxed contentEditable: a page can plant executable markup on the clipboard
      // ("pastejacking" — overriding copy/cut or using navigator.clipboard.write), and nothing
      // server-side sanitizes this until the form is submitted. Insert only plain text.
      e.preventDefault();
      insertPlainTextAtCursor(e.clipboardData?.getData("text/plain") ?? "");
    },
    [processImageFile, onError, insertPlainTextAtCursor]
  );

  const handleDrop = useCallback((e) => {
    const imageFile = allowImages
      ? Array.from(e.dataTransfer?.files || []).find((file) => file.type.startsWith("image/"))
      : null;
    if (imageFile) {
      e.preventDefault();
      processImageFile(imageFile);
      return;
    }
    if (!e.dataTransfer?.types?.includes("text/plain")) return; // e.g. an unsupported file drop — no safe fallback content
    // Same pastejacking-shaped risk as handlePaste: never let the browser insert dragged
    // text/html into this live, un-sandboxed contentEditable.
    e.preventDefault();
    insertPlainTextAtCursor(e.dataTransfer.getData("text/plain") || "");
  }, [allowImages, processImageFile, insertPlainTextAtCursor]);

  const runCommand = useCallback((command, commandValue = null) => {
    ref.current?.focus();
    document.execCommand(command, false, commandValue);
    emitChange();
  }, [emitChange]);

  const addLink = useCallback(() => {
    const url = window.prompt("כתובת הקישור (https://)");
    if (!url) return;
    runCommand("createLink", url);
  }, [runCommand]);

  return (
    <div className="space-y-1">
      {(allowImages || allowFormatting) && (
        <div className="flex flex-wrap gap-1">
          {allowFormatting && (
            <>
              <button type="button" onClick={() => runCommand("bold")} className="text-xs border rounded-md px-2 py-1 font-bold hover:bg-gray-50">B</button>
              <button type="button" onClick={() => runCommand("italic")} className="text-xs border rounded-md px-2 py-1 italic hover:bg-gray-50">I</button>
              <button type="button" onClick={() => runCommand("insertUnorderedList")} className="text-xs border rounded-md px-2 py-1 hover:bg-gray-50">• רשימה</button>
              <button type="button" onClick={() => runCommand("insertOrderedList")} className="text-xs border rounded-md px-2 py-1 hover:bg-gray-50">1. רשימה</button>
              <button type="button" onClick={addLink} className="text-xs border rounded-md px-2 py-1 hover:bg-gray-50">קישור</button>
            </>
          )}
          {allowImages && (
            <>
          <button type="button" onClick={() => fileInputRef.current?.click()} className="text-xs border rounded-md px-2 py-1 text-brand-700 hover:bg-brand-50">
            הוסף תמונה
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            className="hidden"
            onChange={(event) => {
              processImageFile(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
            </>
          )}
        </div>
      )}
      <div
        id={id}
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        autoFocus={autoFocus}
        dir="auto"
        onInput={emitChange}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={(event) => allowImages && event.preventDefault()}
        onBlur={emitChange}
        data-placeholder={placeholder}
        className={`rte-editable w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand-500 overflow-auto ${className}`}
        style={{ minHeight: `${minRows * 1.5}rem` }}
      />
    </div>
  );
}
